import React from 'react';
import { Container, Row, Col, Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: 'fas fa-tasks',
      title: 'Complete Tasks',
      description: 'Earn rewards by completing simple tasks and challenges'
    },
    {
      icon: 'fas fa-coins',
      title: 'Earn Rewards',
      description: 'Get paid for your participation and task completion'
    },
    {
      icon: 'fas fa-users',
      title: 'Refer Friends',
      description: 'Invite friends and earn bonus rewards for successful referrals'
    },
    {
      icon: 'fas fa-chart-line',
      title: 'Track Progress',
      description: 'Monitor your earnings and achievements in real-time'
    },
    {
      icon: 'fas fa-shield-alt',
      title: 'Secure Platform',
      description: 'Your data and earnings are protected with top-tier security'
    },
    {
      icon: 'fas fa-mobile-alt',
      title: 'Mobile Friendly',
      description: 'Access your dashboard anywhere, anytime from any device'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <div className="hero-section">
        <Container>
          <Row className="align-items-center min-vh-100">
            <Col lg={6}>
              <div className="hero-content">
                <h1 className="display-4 fw-bold mb-4">
                  Welcome to <span style={{color: '#ffc107'}}>Networqpay</span>
                </h1>
                <h2 className="display-5 fw-bold mb-3">
                  Stop Scrolling. Start Earning.
                </h2>
                <p className="lead mb-4" style={{color: '#ffc107'}}>
                  Monetize Your Free Time by completing simple tasks.
                </p>
                <div className="hero-stats mb-4">
                  <Row>
                    <Col xs={4} className="text-center">
                      <div className="stat-number">Easy</div>
                      <div className="stat-label">Registration</div>
                    </Col>
                    <Col xs={4} className="text-center">
                      <div className="stat-number">Easy</div>
                      <div className="stat-label">Tasks</div>
                    </Col>
                    <Col xs={4} className="text-center">
                      <div className="stat-number">99%</div>
                      <div className="stat-label">Success Ratio</div>
                    </Col>
                  </Row>
                </div>
                <div className="hero-buttons">
                  <Button 
                    variant="primary" 
                    size="lg" 
                    className="me-3 px-4"
                    onClick={() => navigate('/register')}
                  >
                    Get Started Free
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    size="lg" 
                    className="px-4"
                    onClick={() => navigate('/login')}
                  >
                    Sign In
                  </Button>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="hero-image">
                <div className="hero-card">
                  <Card className="shadow-lg border-0">
                    <Card.Body className="p-4">
                      <div className="text-center mb-3">
                        <i className="fas fa-trophy text-warning" style={{fontSize: '3rem'}}></i>
                      </div>
                      <h5 className="text-center mb-3">Networqpay</h5>
                      <h6 className="text-center mb-3 text-primary">Unlock the Referral Goldmine!</h6>
                      <p className="text-center mb-3 small">
                        Invite just a few motivated friends and watch the bonus earnings effortlessly.
                      </p>
                      <div className="demo-content">
                        <img src={`${process.env.PUBLIC_URL}/main.jpeg`} alt="Demo" className="img-fluid" />
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Features Section */}
      <section className="features-section py-5" id="features">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="section-title">Why Choose NetworqPay?</h2>
              <p className="section-subtitle text-muted">
                We don't just offer tasks. We offer tangible results and a path to financial growth.
              </p>
            </Col>
          </Row>
          <Row>
            {features.map((feature, index) => (
              <Col md={6} lg={4} key={index} className="mb-4">
                <Card className="h-100 border-0 shadow-sm feature-card">
                  <Card.Body className="text-center p-4">
                    <div className="feature-icon mb-3">
                      <i className={`${feature.icon} text-primary`} style={{fontSize: '2.5rem'}}></i>
                    </div>
                    <h5 className="feature-title mb-3">{feature.title}</h5>
                    <p className="feature-description text-muted">{feature.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* How it Works Section */}
      <section className="how-it-works-section py-5 bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="section-title">How It Works</h2>
              <p className="section-subtitle text-muted">
                Get started in just 3 simple steps
              </p>
            </Col>
          </Row>
          <Row>
            <Col md={4} className="text-center mb-4">
              <div className="step-circle mx-auto mb-3" style={{backgroundColor: '#ffc107'}}>1</div>
              <h5>Claim</h5>
              <p className="text-muted">Your free account</p>
            </Col>
            <Col md={4} className="text-center mb-4">
              <div className="step-circle mx-auto mb-3" style={{backgroundColor: '#ffc107'}}>2</div>
              <h5>Conquer</h5>
              <p className="text-muted">Your first task</p>
            </Col>
            <Col md={4} className="text-center mb-4">
              <div className="step-circle mx-auto mb-3" style={{backgroundColor: '#ffc107'}}>3</div>
              <h5>Cash Out</h5>
              <p className="text-muted">Your rewards</p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h2 className="mb-4">Ready to Start Earning?</h2>
              <p className="lead mb-4">
                Are you ready to turn your free time into income? The opportunity is NOW! Join thousands of Users who are actively building a supplementary income stream with NetworqPay. Don't Wait. Start Today!
              </p>
              <Button 
                variant="primary" 
                size="lg" 
                className="px-5 py-3"
                onClick={() => navigate('/register')}
              >
                Start Earning Now
                <i className="fas fa-arrow-right ms-2"></i>
              </Button>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default LandingPage;
